/**
 * Journey-Service (J-S::B2C) API
 * ## SBB Journey-Planner  (de:Fahrplanpublikation Personenverkehr SBB)  This is a stateless RESTful web-service for **timetable based public transportation routing** as provided by SBB Journey-Planner applications such as [sbb.ch](https://www.sbb.ch/) or [SBB Mobile App](https://apps.apple.com/ch/app/sbb-mobile/id294855237). The service-contract is restricted to **SBB internal consumers** or **accredited partners** and supports various and enhanced use cases.  ### Before \'Sign up\'  For new consumers: 1. This **service needs official approval** before granting any access, therefore send an e-mail (see \'Contact -> E-Mail\') first with the subject: **Request: Sign Up Journey-Service** 2. Wait for the appropriate response e-mail and fill-in the attached **Questionnaire** and follow further steps given in the response.    ### Approved and \'Signed up\' consumers See **Contact** box * **Homepage** for general infos about the service  * **Developer resources** for specific usage and concepts of Journey-Service  See **Blog** on PRODuction for Release-Notes.  Remark: * Please read the **Service-Contract** description in Swagger-Doc carefully. * For further questions, send an e-Mail.  
 *
 * The version of the OpenAPI document: 3.20.37
 * Contact: journey-service@sbb.ch
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
import { CoordinatesWGS84 } from './coordinatesWGS84';


/**
 * A stop represents a specific location (typically a STATION) of a leg or a complete journey-detail of a transport-product (aka v3.ScheduledStopPoint, OJP StopPoint).
 */
export interface StopV2 { 
    /**
     * Origin and Destination: STATION, ADDRESS or POI; STATION: any stop between Origin and Destination
     */
    type: StopV2.TypeEnum;
    /**
     * Unique identifier of STATION (case uic) or ADDRESS/POI (case id).
     */
    uicOrId: string;
    /**
     * Name of STATION (locale naming, non- translated).
     */
    name: string;
    coordinates?: CoordinatesWGS84;
    /**
     * Canton abbreviation for locations within Switzerland.
     */
    cantonCH?: string;
    /**
     * Route index of a stop/station in a journey-segment (depends on StopBehaviour). Usually starting from 0 or 1 and incrementing by 1. However if the route index value jumps: - it is most likely that the journey was rerouted - or a virtual Stop
     */
    routeIndex?: number;
    /**
     * Provides the type of the prognosis like if it was reported by an external provider or calculated or corrected by the system. Related to arrival date/time.
     */
    arrivalPrognosisType?: StopV2.ArrivalPrognosisTypeEnum;
    /**
     * Provides the type of the prognosis like if it was reported by an external provider or calculated or corrected by the system. Related to departure date/time.
     */
    departurePrognosisType?: StopV2.DeparturePrognosisTypeEnum;
    /**
     * Status at stop if STATION (like on plan, cancelled, partially cancelled). Null if ADDRESS or POI. This is is useful when iterating from stop to stop getting appropriate state symbols (de:Perlenschnur), see [Routing-Basics](https://github.com/SchweizerischeBundesbahnen/journey-service/blob/master/business/RoutingBasics.md).
     */
    stopStatus: StopV2.StopStatusEnum;
    /**
     * Boarding (de:einsteigen) and alighting (de:aussteigen) status for this Stop; see [Routing-Basics](https://github.com/SchweizerischeBundesbahnen/journey-service/blob/master/business/RoutingBasics.md)
     */
    boardingAlightingStatus: StopV2.BoardingAlightingStatusEnum;
    /**
     * @Deprecated use boardingAlightingStatus exclusively
     */
    boarding?: boolean;
    /**
     * @Deprecated use boardingAlightingStatus exclusively
     */
    boardingRt?: boolean;
    /**
     * @Deprecated use boardingAlightingStatus exclusively
     */
    alighting?: boolean;
    /**
     * @Deprecated use boardingAlightingStatus exclusively
     */
    alightingRt?: boolean;
    /**
     * Touristic \'marker\' like \'Bahn2000-Strecke\' where boarding/alighting is not possible.
     */
    virtual?: boolean;
    /**
     * @Deprecated Check StopStatus.NOT_SERVICED instead.
     */
    notServiced: boolean;
    /**
     * Passenger needs to call for a vehicle stop (aka OJP `RequestStop`, resp. de:Halt auf Verlangen).
     */
    requestStop: boolean;
    /**
     * Arriving platform planned.
     */
    arrivalTrack?: string;
    /**
     * Arriving platform in realtime (oversteers arrivalTrack!). Whether this means a realtime change or just more detailed information has to be checked in related arrivalPlatformChanged!
     */
    arrivalTrackRt?: string;
    /**
     * Related to arrivalTrackRt and arrivalTrack. **false** even if the rt track is set, this is not considered a realtime change (acc. to SBB busines rules); **true** real track change (typically drawn red in SBB UIs).
     */
    arrivalPlatformChanged: boolean;
    /**
     * Departing platform planned.
     */
    departureTrack?: string;
    /**
     * Principally same as departingTrack but with direction specific boarding-sections. Useful for e.g. in wing-trains (de:Flügelzug) such as Bern-Zweisimmen with a split of partial compositions at Spiez where customers might reach the wrong destination if they board the wrong boarding car. For end-customer-info prefer this value to display in UIs.
     */
    departureTrackFormatted?: string;
    /**
     * Departing platform in realtime (oversteers departureTrack!). Whether this means a realtime change or just more detailed information has to be checked in related departurePlatformChanged!
     */
    departureTrackRt?: string;
    /**
     * Related to departureTrackRt and departureTrack. **false** even if the rt track is set, this is not considered a realtime change (acc. to SBB busines rules); **true** real track change (typically drawn red in SBB UIs).
     */
    departurePlatformChanged: boolean;
    /**
     * @Deprecated use stopStatus exclusively
     */
    unplanned?: boolean;
    /**
     * @Deprecated use stopStatus exclusively
     */
    cancelled?: boolean;
    /**
     * Arrival date/time ([ISO-8601](https://datatracker.ietf.org/doc/html/rfc3339#section-5.6) UTC with time-numoffset (like \'+02:00\')) planned.
     */
    arrivalDateTime?: string;
    /**
     * Arrival date/time ([ISO-8601](https://datatracker.ietf.org/doc/html/rfc3339#section-5.6) UTC with time-numoffset (like \'+02:00\')) in realtime (oversteers `arrivalDateTime`!).
     */
    arrivalDateTimeRt?: string;
    /**
     * Formatted end-user message about delay at arrival, according to SBB business rules.
     */
    arrivalDelayText?: string;
    /**
     * Departure date/time ([ISO-8601](https://datatracker.ietf.org/doc/html/rfc3339#section-5.6) UTC with time-numoffset (like \'+02:00\')) planned.
     */
    departureDateTime?: string;
    /**
     * Departure date/time ([ISO-8601](https://datatracker.ietf.org/doc/html/rfc3339#section-5.6) UTC with time-numoffset (like \'+02:00\')) in realtime (oversteers `departureDateTime`!).
     */
    departureDateTimeRt?: string;
    /**
     * Realtime delay message at departure. Formatted to display for end-user according to SBB business rules.
     */
    departureDelayText?: string;
    /**
     * true: After a long amount of delay, according to SBB business rules (useful for channel to present the appropriate symbol).
     */
    delayUndefined: boolean;
    /**
     * 1st class de:Belegung (average)
     */
    avgOccupancyFirstClass: StopV2.AvgOccupancyFirstClassEnum;
    /**
     * 2nd class de:Belegung (average)
     */
    avgOccupancySecondClass: StopV2.AvgOccupancySecondClassEnum;
    accessibility?: Array<StopV2.AccessibilityEnum>;
    /**
     * The most relevant accessibility restriction for handicaped people out of #accessibility (according to SBB business rules).
     */
    accessibilityMostRelevant?: StopV2.AccessibilityMostRelevantEnum;
    /**
     * Exit side within a vehicle at a Stop (for e.g. car of train-formation) in relation to driving/forward direction.
     */
    exitSide?: StopV2.ExitSideEnum;
    /**
     * Rank of importance based on intermediate stop priority and distance within a (partial) JourneyDetail. SBB KI rule based on display-info to find relevant intermediate stops to present on a visual stationboard (de:Perronanzeiger). Origin and destination of departure/arrival have always rank=null. Depends on /v2/departures or /v2/arrivals includeRank=true
     */
    rank?: number;
    /**
     * null irrelevant
     */
    tariffBorderPoint?: boolean;
}
export namespace StopV2 {
    export type TypeEnum = 'STATION' | 'ADDRESS' | 'POI';
    export const TypeEnum = {
        Station: 'STATION' as TypeEnum,
        Address: 'ADDRESS' as TypeEnum,
        Poi: 'POI' as TypeEnum
    };
    export type ArrivalPrognosisTypeEnum = 'PROGNOSED' | 'MANUAL' | 'REPORTED' | 'CORRECTED' | 'CALCULATED';
    export const ArrivalPrognosisTypeEnum = {
        Prognosed: 'PROGNOSED' as ArrivalPrognosisTypeEnum,
        Manual: 'MANUAL' as ArrivalPrognosisTypeEnum,
        Reported: 'REPORTED' as ArrivalPrognosisTypeEnum,
        Corrected: 'CORRECTED' as ArrivalPrognosisTypeEnum,
        Calculated: 'CALCULATED' as ArrivalPrognosisTypeEnum
    };
    export type DeparturePrognosisTypeEnum = 'PROGNOSED' | 'MANUAL' | 'REPORTED' | 'CORRECTED' | 'CALCULATED';
    export const DeparturePrognosisTypeEnum = {
        Prognosed: 'PROGNOSED' as DeparturePrognosisTypeEnum,
        Manual: 'MANUAL' as DeparturePrognosisTypeEnum,
        Reported: 'REPORTED' as DeparturePrognosisTypeEnum,
        Corrected: 'CORRECTED' as DeparturePrognosisTypeEnum,
        Calculated: 'CALCULATED' as DeparturePrognosisTypeEnum
    };
    export type StopStatusEnum = 'PLANNED' | 'CANCELLED' | 'BEGIN_PARTIAL_CANCELLATION' | 'END_PARTIAL_CANCELLATION' | 'NOT_SERVICED' | 'UNPLANNED';
    export const StopStatusEnum = {
        Planned: 'PLANNED' as StopStatusEnum,
        Cancelled: 'CANCELLED' as StopStatusEnum,
        BeginPartialCancellation: 'BEGIN_PARTIAL_CANCELLATION' as StopStatusEnum,
        EndPartialCancellation: 'END_PARTIAL_CANCELLATION' as StopStatusEnum,
        NotServiced: 'NOT_SERVICED' as StopStatusEnum,
        Unplanned: 'UNPLANNED' as StopStatusEnum
    };
    export type BoardingAlightingStatusEnum = 'BOARDING' | 'ALIGHTING' | 'BOARDING_ALIGHTING' | 'IRRELEVANT' | 'NONE';
    export const BoardingAlightingStatusEnum = {
        Boarding: 'BOARDING' as BoardingAlightingStatusEnum,
        Alighting: 'ALIGHTING' as BoardingAlightingStatusEnum,
        BoardingAlighting: 'BOARDING_ALIGHTING' as BoardingAlightingStatusEnum,
        Irrelevant: 'IRRELEVANT' as BoardingAlightingStatusEnum,
        None: 'NONE' as BoardingAlightingStatusEnum
    };
    export type AvgOccupancyFirstClassEnum = 'UNKNOWN' | 'LOW' | 'MEDIUM' | 'HIGH';
    export const AvgOccupancyFirstClassEnum = {
        Unknown: 'UNKNOWN' as AvgOccupancyFirstClassEnum,
        Low: 'LOW' as AvgOccupancyFirstClassEnum,
        Medium: 'MEDIUM' as AvgOccupancyFirstClassEnum,
        High: 'HIGH' as AvgOccupancyFirstClassEnum
    };
    export type AvgOccupancySecondClassEnum = 'UNKNOWN' | 'LOW' | 'MEDIUM' | 'HIGH';
    export const AvgOccupancySecondClassEnum = {
        Unknown: 'UNKNOWN' as AvgOccupancySecondClassEnum,
        Low: 'LOW' as AvgOccupancySecondClassEnum,
        Medium: 'MEDIUM' as AvgOccupancySecondClassEnum,
        High: 'HIGH' as AvgOccupancySecondClassEnum
    };
    export type AccessibilityEnum = 'ALL' | 'BOARDING_ALIGHTING_SELF' | 'BOARDING_ALIGHTING_BY_CREW' | 'BOARDING_ALIGHTING_BY_NOTIFICATION' | 'BOARDING_ALIGHTING_SHUTTLE_TRANSPORT' | 'BOARDING_ALIGHTING_NOT_POSSIBLE' | 'BOARDING_NEEDS_INFONOTE' | 'BOARDING_NEEDS_RESERVATIONNOTE';
    export const AccessibilityEnum = {
        All: 'ALL' as AccessibilityEnum,
        BoardingAlightingSelf: 'BOARDING_ALIGHTING_SELF' as AccessibilityEnum,
        BoardingAlightingByCrew: 'BOARDING_ALIGHTING_BY_CREW' as AccessibilityEnum,
        BoardingAlightingByNotification: 'BOARDING_ALIGHTING_BY_NOTIFICATION' as AccessibilityEnum,
        BoardingAlightingShuttleTransport: 'BOARDING_ALIGHTING_SHUTTLE_TRANSPORT' as AccessibilityEnum,
        BoardingAlightingNotPossible: 'BOARDING_ALIGHTING_NOT_POSSIBLE' as AccessibilityEnum,
        BoardingNeedsInfonote: 'BOARDING_NEEDS_INFONOTE' as AccessibilityEnum,
        BoardingNeedsReservationnote: 'BOARDING_NEEDS_RESERVATIONNOTE' as AccessibilityEnum
    };
    export type AccessibilityMostRelevantEnum = 'ALL' | 'BOARDING_ALIGHTING_SELF' | 'BOARDING_ALIGHTING_BY_CREW' | 'BOARDING_ALIGHTING_BY_NOTIFICATION' | 'BOARDING_ALIGHTING_SHUTTLE_TRANSPORT' | 'BOARDING_ALIGHTING_NOT_POSSIBLE' | 'BOARDING_NEEDS_INFONOTE' | 'BOARDING_NEEDS_RESERVATIONNOTE';
    export const AccessibilityMostRelevantEnum = {
        All: 'ALL' as AccessibilityMostRelevantEnum,
        BoardingAlightingSelf: 'BOARDING_ALIGHTING_SELF' as AccessibilityMostRelevantEnum,
        BoardingAlightingByCrew: 'BOARDING_ALIGHTING_BY_CREW' as AccessibilityMostRelevantEnum,
        BoardingAlightingByNotification: 'BOARDING_ALIGHTING_BY_NOTIFICATION' as AccessibilityMostRelevantEnum,
        BoardingAlightingShuttleTransport: 'BOARDING_ALIGHTING_SHUTTLE_TRANSPORT' as AccessibilityMostRelevantEnum,
        BoardingAlightingNotPossible: 'BOARDING_ALIGHTING_NOT_POSSIBLE' as AccessibilityMostRelevantEnum,
        BoardingNeedsInfonote: 'BOARDING_NEEDS_INFONOTE' as AccessibilityMostRelevantEnum,
        BoardingNeedsReservationnote: 'BOARDING_NEEDS_RESERVATIONNOTE' as AccessibilityMostRelevantEnum
    };
    export type ExitSideEnum = 'LEFT' | 'RIGHT' | 'UNKNOWN';
    export const ExitSideEnum = {
        Left: 'LEFT' as ExitSideEnum,
        Right: 'RIGHT' as ExitSideEnum,
        Unknown: 'UNKNOWN' as ExitSideEnum
    };
}


